import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  ArrowLeft,
  RefreshCw,
  Server,
  Database,
  Globe,
  Shield,
  Clock,
  Activity
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'

interface SystemComponent {
  id: string
  name: string
  status: 'operational' | 'degraded' | 'outage'
  description: string
  lastChecked: string
  responseTime?: number
  uptime?: number
}

interface Incident {
  id: string
  title: string
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved'
  description: string
  startedAt: string
  resolvedAt?: string
  affectedServices: string[]
}

const systemComponents: SystemComponent[] = [
  {
    id: 'api',
    name: 'API Services',
    status: 'operational',
    description: 'Core API endpoints and data services',
    lastChecked: '2024-01-15T10:30:00Z',
    responseTime: 120,
    uptime: 99.9
  },
  {
    id: 'database',
    name: 'Database',
    status: 'operational',
    description: 'Primary database and data storage',
    lastChecked: '2024-01-15T10:30:00Z',
    responseTime: 45,
    uptime: 99.95
  },
  {
    id: 'cdn',
    name: 'CDN',
    status: 'operational',
    description: 'Content delivery network for static assets',
    lastChecked: '2024-01-15T10:30:00Z',
    responseTime: 25,
    uptime: 99.8
  },
  {
    id: 'auth',
    name: 'Authentication',
    status: 'operational',
    description: 'User authentication and authorization',
    lastChecked: '2024-01-15T10:30:00Z',
    responseTime: 80,
    uptime: 99.9
  },
  {
    id: 'storage',
    name: 'File Storage',
    status: 'degraded',
    description: 'File upload and storage services',
    lastChecked: '2024-01-15T10:30:00Z',
    responseTime: 250,
    uptime: 98.5
  },
  {
    id: 'notifications',
    name: 'Notifications',
    status: 'operational',
    description: 'Email and push notification services',
    lastChecked: '2024-01-15T10:30:00Z',
    responseTime: 150,
    uptime: 99.7
  }
]

const recentIncidents: Incident[] = [
  {
    id: '1',
    title: 'File Upload Issues',
    status: 'monitoring',
    description: 'Some users experiencing slow file uploads',
    startedAt: '2024-01-15T08:00:00Z',
    affectedServices: ['File Storage', 'CDN']
  },
  {
    id: '2',
    title: 'Database Maintenance',
    status: 'resolved',
    description: 'Scheduled maintenance completed successfully',
    startedAt: '2024-01-14T02:00:00Z',
    resolvedAt: '2024-01-14T04:00:00Z',
    affectedServices: ['Database', 'API Services']
  }
]

export function SystemStatusPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(new Date())

  const refreshStatus = async () => {
    setIsRefreshing(true)
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      setLastUpdated(new Date())
    } catch (error) {
      console.error('Refresh error:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'degraded':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'outage':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800'
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800'
      case 'outage':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getIncidentStatusColor = (status: string) => {
    switch (status) {
      case 'investigating':
        return 'bg-red-100 text-red-800'
      case 'identified':
        return 'bg-orange-100 text-orange-800'
      case 'monitoring':
        return 'bg-yellow-100 text-yellow-800'
      case 'resolved':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getOverallStatus = () => {
    const hasOutage = systemComponents.some(comp => comp.status === 'outage')
    const hasDegraded = systemComponents.some(comp => comp.status === 'degraded')
    
    if (hasOutage) return { status: 'outage', text: 'Service Outage' }
    if (hasDegraded) return { status: 'degraded', text: 'Degraded Performance' }
    return { status: 'operational', text: 'All Systems Operational' }
  }

  const overallStatus = getOverallStatus()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/more')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Status</h1>
            <p className="text-gray-600">Check system performance and status</p>
          </div>
        </div>
        <Button
          onClick={refreshStatus}
          disabled={isRefreshing}
          variant="outline"
        >
          {isRefreshing ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Overall Status */}
      <Card className={`p-6 border-l-4 ${
        overallStatus.status === 'operational' ? 'border-green-500 bg-green-50' :
        overallStatus.status === 'degraded' ? 'border-yellow-500 bg-yellow-50' :
        'border-red-500 bg-red-50'
      }`}>
        <div className="flex items-center space-x-4">
          {getStatusIcon(overallStatus.status)}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{overallStatus.text}</h2>
            <p className="text-gray-600">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* System Components */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Components</h3>
            <div className="space-y-4">
              {systemComponents.map((component) => (
                <div key={component.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(component.status)}
                    <div>
                      <h4 className="font-medium text-gray-900">{component.name}</h4>
                      <p className="text-sm text-gray-600">{component.description}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-500">
                          Last checked: {formatTime(component.lastChecked)}
                        </span>
                        {component.responseTime && (
                          <span className="text-xs text-gray-500">
                            Response: {component.responseTime}ms
                          </span>
                        )}
                        {component.uptime && (
                          <span className="text-xs text-gray-500">
                            Uptime: {component.uptime}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge className={getStatusColor(component.status)}>
                    {component.status}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Status Summary */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Operational</span>
                <Badge className="bg-green-100 text-green-800">
                  {systemComponents.filter(c => c.status === 'operational').length}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Degraded</span>
                <Badge className="bg-yellow-100 text-yellow-800">
                  {systemComponents.filter(c => c.status === 'degraded').length}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Outage</span>
                <Badge className="bg-red-100 text-red-800">
                  {systemComponents.filter(c => c.status === 'outage').length}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Recent Incidents */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Incidents</h3>
            <div className="space-y-3">
              {recentIncidents.map((incident) => (
                <div key={incident.id} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{incident.title}</h4>
                    <Badge className={getIncidentStatusColor(incident.status)}>
                      {incident.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{incident.description}</p>
                  <div className="text-xs text-gray-500">
                    Started: {formatTime(incident.startedAt)}
                    {incident.resolvedAt && (
                      <span> • Resolved: {formatTime(incident.resolvedAt)}</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <span className="text-xs text-gray-500">Affected: </span>
                    <span className="text-xs text-gray-700">
                      {incident.affectedServices.join(', ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Performance Metrics */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
        <div className="grid gap-4 md:grid-cols-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">99.9%</div>
            <div className="text-sm text-gray-600">Overall Uptime</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">120ms</div>
            <div className="text-sm text-gray-600">Avg Response Time</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">1,234</div>
            <div className="text-sm text-gray-600">Active Users</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">45.6GB</div>
            <div className="text-sm text-gray-600">Data Processed</div>
          </div>
        </div>
      </Card>

      {/* Status Page Link */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Public Status Page</h3>
          <p className="text-blue-700 mb-4">
            View our public status page for real-time updates and incident reports.
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Globe className="h-4 w-4 mr-2" />
            View Public Status Page
          </Button>
        </div>
      </Card>
    </div>
  )
}
